import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../core/common/copyRightInfo";
import BillingAddressModal from "../../../core/common/billingAddressModal";
import { RAZORPAY_CONFIG, formatAmountForDisplay } from "../../../common/RazorpayConfig";
import useFetch from "../../../hooks/useFetch";
import { getAPIHeaders, getTenant, getUserProfile } from "../../../common/Utils";
import { API } from "../../../common/Constants";
import { PaymentDetails , Order, Subscription, BillingAddressData } from "../../../core/data/interface";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../subscriptions/subscriptions.css";
import { DEFAULT_THUMBNAIL_URL } from "../../../environment";
import PreLoader from "../../../core/common/preLoader";
import CustomToast from "../../../core/common/customToast";

// Helper function to count study materials for a single course
const countCourseStudyMaterials = (course: any) => {
  let videos = 0;
  let documents = 0;
  let links = 0;
  let tests = 0;
  
  course.courseDetails?.subjects?.forEach((subject: any) => {
    subject.syllabus.forEach((syllabus: any) => {
      syllabus.studyMaterials.forEach((material: any) => {
        switch (material.materialType) {
          case 'VIDEO':
            videos++;
            break;
          case 'NOTE':
          case 'FILE':
            documents++;
            break;
          case 'LINK':
            // Check if it's a YouTube link (including YouTube Shorts)
            if (material.content && (material.content.includes('youtube.com') || material.content.includes('youtu.be') || material.content.includes('m.youtube.com'))) {
              videos++;
            } else {
              links++;
            }
            break;
        }
      });
    });
  });
  
  return { videos, documents, links, tests };
};

// Helper function to calculate discount percentage
const calculateDiscountPercentage = (originalPrice: number, promotionalPrice: number) => {
  if (originalPrice <= 0) return 0;
  return Math.round(((originalPrice - promotionalPrice) / originalPrice) * 100);
};

// Helper function to get unique course thumbnails for carousel
const getCourseThumbnails = (subscription: Subscription) => {
  return subscription?.courses.map(course => ({
    courseId: course.courseId,
    courseTitle: course.courseTitle,
    thumbnail: course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL
  }));
};

// Helper function to render stars for rating
const renderStars = (rating: number) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0; i < fullStars; i++) {
    stars.push(<i key={i} className="fas fa-star text-warning"></i>);
  }

  if (hasHalfStar) {
    stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
  }

  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<i key={`empty-${i}`} className="far fa-star text-warning"></i>);
  }

  return stars;
};

const OrderSubscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [availableSubscriptions, setAvailableSubscriptions] = useState<Subscription[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<Subscription[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [orderDetails, setOrderDetails] = useState<Order | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Toast state
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "",
    heading: "Payment Error",
    type: "ERROR",
  });
  
  // Billing address state
  const [billingAddress, setBillingAddress] = useState<BillingAddressData | null>(null);
  const [hasBillingAddress, setHasBillingAddress] = useState<boolean | null>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  
  // API call setup
  const { isLoading, error, callFetch } = useFetch();

  // Razorpay instance reference
  const razorpayInstanceRef = useRef<any>(null);

  // Initialize Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchSubscriptionData();
    checkBillingAddress();
  }, []);

  const fetchSubscriptionData = () => {
    callFetch(
      API.SUBCRIPTION_HOME_DATA,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response)=>{      
      setAvailableSubscriptions(response.availableSubscriptionPlans);
      setUserSubscriptions(response.userSubscriptionInfo?.activeSubscriptions || []);
    }).catch((err) => {
      console.error('Failed to fetch subscriptions:', err);
    });
  };

  const checkBillingAddress = () => {
    callFetch(
      API.ADDRESS,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      // Check if response is an array and if it's empty
      if (Array.isArray(response) && response?.length === 0) {
        setHasBillingAddress(false);
        setBillingAddress(null);
        return;
      }
      
      if (response && response.length > 0) {
        const billingAddr = response.find((addr: any) => addr.addressTypeCode === "BILLING");
        if (billingAddr) {
          setBillingAddress(billingAddr);
          setHasBillingAddress(true);
        } else {
          setHasBillingAddress(false);
          setBillingAddress(null);
        }
      } else {
        setHasBillingAddress(false);
        setBillingAddress(null);
      }
    }).catch((err) => {
      setHasBillingAddress(false);
      setBillingAddress(null);
    });
  };

  useEffect(() => {
    if (subscription?.subscriptionPlanId) {
      fetchSubscriptionPricing(subscription?.subscriptionPlanId?.toString());
    }
  }, [subscription]);


  const fetchSubscriptionPricing = (subscriptionPlanId: string) => {
    callFetch(
      API.SUBSCRIPTION_PRICING.replace(":subscriptionPlanId", subscriptionPlanId),
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response)=>{      
      setPaymentDetails(response);
    }).catch((err) => {
      console.error('Failed to fetch subscription pricing:', err);
    });
  };

  useEffect(() => {
    const subscriptionPlanId = searchParams.get('subscriptionPlanId');
    
    if (subscriptionPlanId) {
      const subscriptionData = availableSubscriptions?.find((subscription: Subscription) => subscription?.subscriptionPlanId === parseInt(subscriptionPlanId));
      if(subscriptionData){
        setSubscription(subscriptionData);
        if (subscriptionData?.courses?.length > 0) {
          setSelectedCourse(subscriptionData.courses[0]);
        }
      } else{
        const userSubscriptionData = userSubscriptions?.find((subscription: Subscription) => subscription?.subscriptionPlanId === parseInt(subscriptionPlanId));
        if(userSubscriptionData){
          setSubscription(userSubscriptionData);
          if (userSubscriptionData?.courses?.length > 0) {
            setSelectedCourse(userSubscriptionData.courses[0]);
          }
        }
      }
    }
  }, [searchParams, availableSubscriptions]);


  // Handle order creation errors
  useEffect(() => {
    if (error) {
      const errorMsg = "Failed to create order. Please try again.";
      setErrorMessage(errorMsg);
      setToastSettings({
        show: true,
        message: errorMsg,
        heading: "Order Error",
        type: "ERROR",
      });
    }
  }, [error]);

  const createOrder = async () => {
    if (!paymentDetails) {
      return;
    }

    if(!orderDetails?.gatewayOrderId){    
      const orderPayload = {
        gateway: "RAZORPAY",
        entityId: subscription?.subscriptionPlanId || "", // Default to "1" if not available
        entityType: "COURSE_SUBSCRIPTION_PLAN",
        notes: subscription?.planName      
      };
        callFetch(
          API.CREATE_ORDER,
          'POST',
          getAPIHeaders(),
          null,              // query parameters (4th parameter)
          orderPayload       // request body (5th parameter)
        ).then((response)=>{
          setOrderDetails(response);
        }).catch((error)=>{
          console.error('Failed to create order:', error);
        });
    } else {
      setOrderDetails({
        ...orderDetails, 
        retry: orderDetails?.retry ? orderDetails?.retry + 1 : 1
      });
    }
  };

  const initializeRazorpayPayment = () => {
    if (!paymentDetails || !orderDetails) {
      return;
    }

    // Check if Razorpay is loaded
    if (!(window as any).Razorpay) {
      const errorMsg = "Payment gateway not loaded. Please refresh the page.";
      setErrorMessage(errorMsg);
      setToastSettings({
        show: true,
        message: errorMsg,
        heading: "Payment Error",
        type: "ERROR",
      });
      return;
    }
    const userProfile = getUserProfile();
    const amountInPaisa = orderDetails.amount * 100;

    const options = {
      key: orderDetails.gatewayKeyId,
      amount: amountInPaisa,
      currency: orderDetails.currency,
      name: "Pathshala Sanchalak",
      description: `Payment for ${subscription?.planName}`,
      order_id: orderDetails.gatewayOrderId,
      readonly: {
        email: true,
        contact: true,
        name: true
      },
        handler: function (response: any) {
          navigate(all_routes.orderStatus, {
            state: {
              subscription,
              paymentDetails,
              orderDetails,
              razorpayResponse: response
            }
          });
        },
      method: {
        card: true,
        netbanking: true,
        upi: true,
        wallet: true,
        emi: orderDetails.supportedPaymentModes?.emi || false,
        paylater: orderDetails.supportedPaymentModes?.paylater || false
      },
      prefill: {
        name: userProfile?.name || userProfile?.firstName,
        email: userProfile?.email ,
        contact: userProfile?.phone || userProfile?.mobile
      },
      notes: {
        subscription_plan: subscription?.planName,
        userEmail: userProfile?.email || "unknown",
        username: userProfile?.username || "unknown",
        tenantId: getTenant() || "unknown"
      },
      theme: {
        color: "#3399cc"
      },
      modal: {
        ondismiss: function() {
          const errorMsg = "Payment was cancelled. Please try again to complete your purchase.";
          setErrorMessage(errorMsg);
          setToastSettings({
            show: true,
            message: errorMsg,
            heading: "Payment Cancelled",
            type: "ERROR",
          });
        }
      }
    };

    try {
      const rzp = new (window as any).Razorpay(options);
      razorpayInstanceRef.current = rzp;
      
      // Handle payment failure
      rzp.on('payment.failed', function (response: any) {
        const errorMsg = `${response?.error?.description || 'Payment failed'} Please try again.`;
        setErrorMessage(errorMsg);
        setToastSettings({
          show: true,
          message: errorMsg,
          heading: "Payment Failed",
          type: "ERROR",
        });
      });

      // Handle other Razorpay errors
      rzp.on('payment.authorization.failed', function (response: any) {
        const errorMsg = `Payment authorization failed. Please try again.`;
        setErrorMessage(errorMsg);
        setToastSettings({
          show: true,
          message: errorMsg,
          heading: "Payment Failed",
          type: "ERROR",
        });
      });

      // Open the payment modal
      rzp.open();
      
    } catch (error) {
      const errorMsg = "Failed to initialize payment gateway. Please try again.";
      setErrorMessage(errorMsg);
      setToastSettings({
        show: true,
        message: errorMsg,
        heading: "Payment Error",
        type: "ERROR",
      });
      // Close modal if it was opened
      if (razorpayInstanceRef.current) {
        razorpayInstanceRef.current.close();
      }
    }
  };

  const handleBillingAddressSaved = (address: BillingAddressData) => {
    setBillingAddress(address);
    setHasBillingAddress(true);
    setShowBillingModal(false);
  };

  const handleRetry = () => {
    setErrorMessage(null);
    setOrderDetails(null);
    window.location.reload();
  };

  const handlePayment = async () => {
    if (!paymentDetails) return;
    if (!hasBillingAddress) {
      setShowBillingModal(true);
      return;
    }
    setErrorMessage(null);
    await createOrder();
  };

  // Handle Razorpay payment when order is created
  useEffect(() => {
    if (orderDetails && orderDetails.gatewayOrderId && orderDetails.gatewayKeyId) { 
      initializeRazorpayPayment();
    } else if (orderDetails && orderDetails.orderStatus === 'PAID') {
      navigate(all_routes.orderStatus, {
        state: {
          subscription,
          paymentDetails,
          orderDetails,
          razorpayResponse: null
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails]);

  const formatPrice = (amount: number, currencyCode: string) => {
    return formatAmountForDisplay(amount, currencyCode);
  };

  const thumbnails = getCourseThumbnails(subscription as Subscription);
  const discountPercentage = calculateDiscountPercentage(paymentDetails?.originalAmount || 0, paymentDetails?.netAmount || 0);
  
  // Carousel settings for course thumbnails
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    adaptiveHeight: true
  };

  if (!paymentDetails || !subscription) {
    return (
      <>
        <PreLoader showLoader={isLoading} />
        <div className="page-wrapper" hidden={isLoading || error}>
          <div className="content">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <i className="ti ti-loader display-1 text-primary mb-3"></i>
                <h4>Creating Order... Please wait</h4>
                <p className="text-muted">Please wait while we create your order.</p>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-wrapper" hidden={!error}>
          <div className="content">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <i className="ti ti-alert-triangle display-1 text-danger mb-3"></i>
                <h4>Error Creating Order</h4>
                <p className="text-muted">{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function renderSubscriptionDetails(paymentDetails: PaymentDetails, subscription: Subscription){
    return (
      <div className="col-lg-7 mb-4">
      <div className="card h-100">
        <div className="card-body">
          {/* Subscription Thumbnail Carousel */}
          <div className="position-relative mb-4">
            {thumbnails.length > 1 ? (
              <Slider {...carouselSettings}>
                {thumbnails.map((thumbnail, index) => {
                  const course = subscription?.courses?.find(c => c.courseId === thumbnail.courseId);
                  return (
                    <div key={index} className="position-relative">
                      <ImageWithBasePath
                        src={thumbnail.thumbnail}
                        alt={thumbnail.courseTitle}
                        className="img-fluid rounded subscription-details-image"
                      />
                      {/* Course Title and Description Overlay */}
                      <div className="position-absolute bottom-0 start-0 end-0 p-3" style={{
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        color: 'white',
                        borderRadius: '0 0 0.375rem 0.375rem'
                      }}>
                        <h5 className="mb-2 fw-bold text-white">{thumbnail.courseTitle}</h5>
                        <p className="mb-0 small text-white-50" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: '1.4'
                        }}>
                          {course?.courseDescription || 'Course description not available'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </Slider>
            ) : (
              <div className="position-relative">
                <ImageWithBasePath
                  src={thumbnails[0]?.thumbnail || DEFAULT_THUMBNAIL_URL}
                  alt={subscription?.planName}
                  className="img-fluid rounded subscription-details-image"
                />
                {/* Course Title and Description Overlay for single image */}
                <div className="position-absolute bottom-0 start-0 end-0 p-3" style={{
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  color: 'white',
                  borderRadius: '0 0 0.375rem 0.375rem'
                }}>
                  <h5 className="mb-2 fw-bold text-white">{thumbnails[0]?.courseTitle || subscription?.planName}</h5>
                  <p className="mb-0 small text-white-50" style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: '1.4'
                  }}>
                    {subscription?.courses[0]?.courseDescription || subscription?.planDescription}
                  </p>
                </div>
              </div>
            )}
            <div className="position-absolute top-0 start-0 p-2">
              <span className="badge bg-primary">{subscription?.planType}</span>
            </div>
          </div>

          {/* Subscription Plan Details */}
          <div className="mb-4">
            <h3 className="mb-2">{subscription?.planName}</h3>
          
            {/* Description */}
            <div className="mb-4">
              <p className="text-muted">{subscription?.planDescription}</p>
            </div>

            <div className="mb-3">
              <div className="p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <div className="fw-medium">{subscription?.duration ? `${subscription?.duration} Months` : 'Lifetime'}</div>
                    <div className="small text-muted">Access Duration</div>
                  </div>
                  <div className="text-end">
                    <div className="d-flex align-items-center">
                      {paymentDetails?.originalAmount > 0 && (
                        <span className="text-decoration-line-through text-muted me-2 small">
                          {paymentDetails?.currencyCode} {paymentDetails?.originalAmount}
                        </span>
                      )}
                      <span className="h5 fw-bold text-primary mb-0">
                        {paymentDetails.currencyCode} {paymentDetails.netAmount}
                      </span>
                    </div>
                    {discountPercentage > 0 && (
                      <div className="small text-success">
                        {discountPercentage}% OFF
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Included Courses */}
          {subscription?.courses.length > 1 && (
            <div className="mb-4">
              <h4>Courses</h4>
              <div className="row g-2 mt-1">
                {subscription?.courses.map(course => (
                  <div key={course.courseId} className="col-md-6">
                    <div 
                      className={`p-3 border rounded cursor-pointer ${selectedCourse?.courseId === course.courseId ? 'border-primary bg-light' : ''}`}
                      onClick={() => setSelectedCourse(course)}
                      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    >
                      <div className="d-flex align-items-center">
                        <ImageWithBasePath
                          src={course.courseMetadata?.thumbnail_url || DEFAULT_THUMBNAIL_URL}
                          alt={course.courseTitle}
                          className="me-3 rounded"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                        />
                        <div>
                          <div className="fw-medium">{course.courseTitle}</div>
                          <small className="text-muted">{course.courseType}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Course Details */}
          {selectedCourse && (
            <div className="mb-4">
              <div className="border rounded p-3 bg-light">
                <h5 className="mb-3">{selectedCourse.courseTitle}</h5>
                
                {/* Rating and Analytics */}
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    {renderStars(selectedCourse.analytics?.averageRating || 0)}
                  </div>
                  <span className="fw-medium me-3">{selectedCourse.analytics?.averageRating?.toFixed(1) || '0.0'}</span>
                  <span className="text-muted">({selectedCourse.analytics?.totalReviews || 0} reviews)</span>
                </div>

                {/* Description */}
                <div className="mb-3">
                  <h6>Description</h6>
                  <p className="text-muted small">{selectedCourse.courseDescription}</p>
                </div>

                {/* Course Materials Details */}
                <div className="mb-3">
                  <h6 className="mb-2">Course Materials</h6>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                          <i className="ti ti-video text-primary me-2" style={{fontSize: '20px'}}></i>
                          <span className="fw-medium">{countCourseStudyMaterials(selectedCourse).videos} Video Lessons</span>
                        </div>
                        <div className="small text-muted">Comprehensive video tutorials covering all topics</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                          <i className="ti ti-file-text text-info me-2" style={{fontSize: '20px'}}></i>
                          <span className="fw-medium">{countCourseStudyMaterials(selectedCourse).documents + countCourseStudyMaterials(selectedCourse).links} Study Materials</span>
                        </div>
                        <div className="small text-muted">PDFs, guides, and reference documents</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="position-relative p-3 bg-white rounded border">
                        <div className="d-flex align-items-center">
                          <i className="ti ti-clipboard-check text-success me-2" style={{fontSize: '20px'}}></i>
                          <span className="fw-medium">{countCourseStudyMaterials(selectedCourse).tests} Tests</span>
                        </div>
                        <div className="small text-muted">Interactive assessments and quizzes</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Course Subjects */}
                {selectedCourse.courseDetails?.subjects?.length > 0 && (
                  <div className="mb-3">
                    <h6 className="mb-2">Course Subjects</h6>
                    <div className="row g-1">
                      {selectedCourse.courseDetails.subjects.map((subject: any) => (
                        <div key={subject.subjectId} className="col-md-6">
                          <div className="p-2 border rounded bg-white">
                            <div className="fw-medium small">{subject.subjectTitle}</div>
                            <small className="text-muted">{subject.subjectDescription}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* What You'll Learn */}
                {selectedCourse.courseMetadata?.learning_highlights?.length && (
                  <div className="mb-3">
                    <h6 className="mb-2">What You'll Learn</h6>
                    <ul className="list-unstyled">
                      {selectedCourse.courseMetadata?.learning_highlights?.map((highlight: string, index: number) => (
                        <li key={index} className="mb-1">
                          <i className="ti ti-check text-success me-2" style={{fontSize: '12px'}}></i>
                          <small>{highlight}</small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="mb-4">
            <h6>Batch Benefits</h6>
            <ul className="list-unstyled mt-1">
              <li className="mb-2">
                <i className="ti ti-check text-success me-2"></i>
                Access to all courses in this batch
              </li>
              <li className="mb-2">
                <i className="ti ti-check text-success me-2"></i>
                {subscription?.duration ? `${subscription?.duration} months` : 'Lifetime'} access
              </li>
              <li className="mb-2">
                <i className="ti ti-check text-success me-2"></i>
                Downloadable study materials
              </li>
              <li className="mb-2">
                <i className="ti ti-check text-success me-2"></i>
                Progress tracking across all courses
              </li>
              <li className="mb-2">
                <i className="ti ti-check text-success me-2"></i>
                Personalized guidance from expert trainers
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    )
  }

  function renderBillingDetials (paymentDetails: PaymentDetails, subscription: Subscription) {
    
    return (
      <div className="col-lg-5 mb-4">
      {/* Billing Address Section */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="ti ti-map-pin me-2"></i>
            Billing Address
          </h5>
        </div>
        <div className="card-body">
          {hasBillingAddress === null ? (
            <div className="text-center p-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 mb-0">Checking billing address...</p>
            </div>
          ) : hasBillingAddress ? (
            <div>
              <div className="alert alert-success mb-3">
                <i className="ti ti-check-circle me-2"></i>
                <strong>Billing Address:</strong> Your billing address is saved and ready for payment.
              </div>
              <div className="address-display p-3 bg-light rounded">
                <p className="mb-1"><strong>Address:</strong> {billingAddress?.addressLine1}</p>
                {billingAddress?.addressLine2 && (
                  <p className="mb-1">{billingAddress.addressLine2}</p>
                )}
                {billingAddress?.landmark && (
                  <p className="mb-1"><strong>Landmark:</strong> {billingAddress.landmark}</p>
                )}
                <p className="mb-1">
                  <strong>City:</strong> {billingAddress?.city}, 
                  <strong> State:</strong> {billingAddress?.stateProvince}, 
                  <strong> Postal Code:</strong> {billingAddress?.postalCode}
                </p>
                <p className="mb-0"><strong>Country:</strong> {billingAddress?.countryCode}</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="alert alert-warning mb-3">
                <i className="ti ti-alert-triangle me-2"></i>
                <strong>Add an address:</strong> Please add your billing address to complete the payment.
              </div>
              <div className="text-center">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowBillingModal(true)}
                >
                  <i className="ti ti-plus me-1"></i>
                  Add Billing Address
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Summary */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="ti ti-receipt me-2"></i>
            Payment Summary
          </h5>
        </div>
        <div className="card-body d-flex flex-column">
          {/* Fees Breakdown */}
          <div className="price-breakdown mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Original Fees:</span>
              <span className="text-decoration-line-through">
                {formatPrice(paymentDetails.originalAmount, paymentDetails.currencyCode)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Discount:</span>
              <span className="text-success fw-medium">
                -{formatPrice(paymentDetails.totalDiscount, paymentDetails.currencyCode)}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Batch Fees:</span>
              <span className="fw-medium">
                {formatPrice(paymentDetails.netAmount, paymentDetails.currencyCode)}
              </span>
            </div>
            
            {/* GST Details - Show only if tax amount is greater than 0 */}
            {paymentDetails.taxAmount > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">GST ({paymentDetails.taxRate}%):</span>
                  <span className="fw-medium">
                    {formatPrice(Math.round(paymentDetails.taxAmount), paymentDetails.currencyCode)}
                  </span>
                </div>
                
                {/* Detailed GST Breakdown - Show only non-zero values */}
                {(paymentDetails.cgstAmount > 0 || paymentDetails.sgstAmount > 0 || paymentDetails.igstAmount > 0) && (
                  <div className=" mb-2 p-2 bg-light rounded small">
                    <div className="text-muted mb-1">
                      <strong>GST Breakdown:</strong>
                    </div>
                    {paymentDetails.cgstAmount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted">CGST ({paymentDetails.cgstRate}%):</span>
                        <span className="fw-medium">
                          {formatPrice(Math.round(paymentDetails.cgstAmount), paymentDetails.currencyCode)}
                        </span>
                      </div>
                    )}
                    {paymentDetails.sgstAmount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted">SGST ({paymentDetails.sgstRate}%):</span>
                        <span className="fw-medium">
                          {formatPrice(Math.round(paymentDetails.sgstAmount), paymentDetails.currencyCode)}
                        </span>
                      </div>
                    )}
                    {paymentDetails.igstAmount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted">IGST ({paymentDetails.igstRate}%):</span>
                        <span className="fw-medium">
                          {formatPrice(Math.round(paymentDetails.igstAmount), paymentDetails.currencyCode)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            
            <hr />
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-bold fs-14">Total Fees:</span>
              <span className="fw-bold fs-16 text-primary">
                {formatPrice(paymentDetails.grandTotal, paymentDetails.currencyCode)}
              </span>
            </div>
          </div>

          {/* Payment Method */}
          <div className="payment-method mb-4">
            <h6 className="mb-3">
              <i className="ti ti-credit-card me-2"></i>
              Payment Method
            </h6>
            <div className="d-flex align-items-center justify-content-center p-3 bg-light rounded">
              <img 
                src="https://badges.razorpay.com/badge-light.png" 
                alt="Razorpay" 
                style={{ height: '30px' }}
                className="me-3"
              />
              <span className="fw-medium">Secure Payment by Razorpay</span>
            </div>
          </div>

          {/* Security Features */}
          <div className="security-features mb-4">
            <div className="row text-center">
              <div className="col-4">
                <i className="ti ti-shield-check text-success mb-1" style={{ fontSize: '1.5rem' }}></i>
                <div className="small text-muted">Secure</div>
              </div>
              <div className="col-4">
                <i className="ti ti-credit-card text-info mb-1" style={{ fontSize: '1.5rem' }}></i>
                <div className="small text-muted">Multiple Options</div>
              </div>
              <div className="col-4">
                <i className="ti ti-clock text-warning mb-1" style={{ fontSize: '1.5rem' }}></i>
                <div className="small text-muted">Instant Access</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-auto">
            {errorMessage && (
              <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                <div className="d-flex align-items-start">
                  <i className="ti ti-alert-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <strong>Payment Error:</strong>
                    <div className="small mt-1">{errorMessage}</div>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setErrorMessage(null)}
                    aria-label="Close"
                  ></button>
                </div>
              </div>
            )}
            <div className="d-grid gap-2">
              {errorMessage ? (
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleRetry}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {'Processing...'}
                    </>
                  ) : (
                    <>
                      <i className="ti ti-refresh me-2"></i>
                      Retry Payment
                    </>
                  )}
                </button>
              ) : (
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handlePayment}
                  disabled={isLoading || !hasBillingAddress}
                  title={!hasBillingAddress ? "Please add billing address first" : ""}
                >
                  {isLoading  ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {'Processing...'}
                    </>
                  ) : !hasBillingAddress ? (
                    <>
                      <i className="ti ti-map-pin me-2"></i>
                      Add Address to Pay
                    </>
                  ) : (
                    <>
                      <i className="ti ti-credit-card me-2"></i>
                      Pay {paymentDetails ? formatPrice(paymentDetails.grandTotal, paymentDetails.currencyCode) : '0'}
                    </>
                  )}
                </button>
              )}
              <Link to={all_routes.subscriptions} className="btn btn-outline-secondary">
                <i className="ti ti-arrow-left me-2"></i>
                Back to Batches
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
    )
  }
  
  return (
    <>
      {/* Page Wrapper */}
      <PreLoader showLoader={isLoading} />
      
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Enroll in Batch</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.index}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.subscriptions}>Batches</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Payment
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          {errorMessage && (
            <div className="alert alert-danger alert-dismissible fade show d-flex align-items-center justify-content-between" role="alert">
              <div className="d-flex align-items-center">
                <i className="ti ti-alert-triangle me-2"></i>
                <span>{errorMessage}</span>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage(null)}
                aria-label="Close"
              ></button>
            </div>
          )}

          {subscription?.enrollmentStatus === 'ACTIVE' && (
            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm mb-4" style={{
                  background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                  borderRadius: '12px'
                }}>
                  <div className="card-body p-4">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <div className="d-flex align-items-start mb-3 mb-md-0">
                          <div className="flex-shrink-0 me-3">
                            <div className="bg-success rounded-circle d-flex align-items-center justify-content-center" style={{
                              width: '60px',
                              height: '60px'
                            }}>
                              <i className="ti ti-check text-white" style={{ fontSize: '28px' }}></i>
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h4 className="mb-2 text-success fw-bold">
                              Active Enrollment
                            </h4>
                            <p className="mb-2 text-dark fs-16">
                              You are currently enrolled in the{' '}
                              <strong className="text-primary">{subscription?.planName}</strong> batch.
                            </p>
                            <p className="mb-0 text-muted small">
                              <i className="ti ti-info-circle me-1"></i>
                              Your enrollment is active and you have full access to all included courses and features.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 text-md-end">
                        <Link 
                          to={all_routes.subscriptions}
                          className="btn btn-primary btn-lg px-4"
                          style={{
                            borderRadius: '8px',
                            fontWeight: '600',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <i className="ti ti-arrow-right me-2"></i>
                          Explore More Batches
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="row" hidden={subscription?.enrollmentStatus === 'ACTIVE'}>
            {/* Left Side - Subscription Details */}
            {paymentDetails && subscription && renderSubscriptionDetails(paymentDetails, subscription)}

            {/* Right Side - Billing Address & Payment Summary */}
            {subscription && paymentDetails && renderBillingDetials(paymentDetails, subscription)}
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <CopyRightInfo/>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Billing Address Modal */}
      <BillingAddressModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        onAddressSaved={handleBillingAddressSaved}
        title="Add Billing Address for Payment"
      />

      {/* Toast Notification */}
      <CustomToast
        show={toastSettings.show}
        message={toastSettings.message}
        heading={toastSettings.heading}
        type={toastSettings.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
        autoFadeTime={5000}
      />
    </>
  );
};

export default OrderSubscription;
